import React, { useRef, useEffect, useState } from 'react';
import { PROFILE, JOURNEY_MILESTONES, EDUCATION } from '../../data/mock';
import { GraduationCap, Briefcase } from 'lucide-react';
import { useIsMobile, useIsTablet } from '../../hooks/useWindowSize';

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const StorySection = () => {
  const [sectionRef, inView] = useInView(0.05);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const pad = isMobile ? '0 20px' : isTablet ? '0 32px' : '0 48px';
  const secPad = isMobile ? '64px 0' : '100px 0';

  return (
    <section id="about" ref={sectionRef} style={{ background: '#F5F3EF', padding: secPad }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: pad }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{ width: '36px', height: '3px', background: '#E62B1E' }} />
          <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, letterSpacing: '3px', color: '#E62B1E', textTransform: 'uppercase' }}>The Story</span>
        </div>

        <h2 style={{
          fontFamily: '"Bebas Neue", sans-serif',
          fontSize: isMobile ? '38px' : 'clamp(40px, 5vw, 64px)',
          color: '#0A0A0A', letterSpacing: '2px',
          margin: '0 0 48px 0', lineHeight: 1
        }}>FROM A STAMMER TO<br /><span style={{ color: '#E62B1E' }}>SHAPING INDUSTRIES</span></h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr' : '1fr 440px',
          gap: isMobile ? '40px' : '72px',
          alignItems: 'start',
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.8s ease, transform 0.8s ease'
        }}>

          {/* Left: text + timeline */}
          <div>
            <p style={{
              fontFamily: 'Inter, sans-serif', fontSize: isMobile ? '15px' : '16px',
              lineHeight: 1.8, color: '#3A3A3A', marginBottom: '40px'
            }}>
              Krupal grew up in Gujarat with a stammer - a challenge that could have defined him.
              Instead, he channelled it into winning a district-level eloquence competition.
              That moment taught him: <strong style={{ color: '#0A0A0A' }}>limitations are just stories we tell ourselves.</strong>
            </p>

            {/* Timeline */}
            <div style={{ position: 'relative' }}>
              <div style={{
                position: 'absolute', left: '28px', top: '8px', bottom: '8px',
                width: '1px', background: 'rgba(10,10,10,0.1)'
              }} />
              {JOURNEY_MILESTONES.map((m, i) => (
                <div key={i} style={{
                  display: 'flex', gap: '24px', marginBottom: '24px',
                  opacity: inView ? 1 : 0,
                  transition: `opacity 0.5s ease ${0.1 + i * 0.07}s`
                }}>
                  <div style={{
                    width: '56px', flexShrink: 0,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px'
                  }}>
                    <div style={{
                      width: '10px', height: '10px', borderRadius: '50%',
                      background: i === JOURNEY_MILESTONES.length - 1 ? '#E62B1E' : '#0A0A0A',
                      border: '2px solid #F5F3EF',
                      boxShadow: `0 0 0 3px ${i === JOURNEY_MILESTONES.length - 1 ? 'rgba(230,43,30,0.2)' : 'rgba(10,10,10,0.08)'}`
                    }} />
                    <span style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '12px', color: '#9A9A9A', letterSpacing: '1px' }}>{m.year}</span>
                  </div>
                  <div style={{ paddingBottom: '6px' }}>
                    <p style={{
                      fontFamily: 'Inter, sans-serif', fontSize: '14px',
                      color: i === JOURNEY_MILESTONES.length - 1 ? '#0A0A0A' : '#3A3A3A',
                      margin: 0, lineHeight: 1.6,
                      fontWeight: i === JOURNEY_MILESTONES.length - 1 ? 600 : 400
                    }}>{m.event}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Education */}
            <div style={{
              marginTop: '40px', background: '#fff', borderRadius: '8px',
              padding: '24px 28px', border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 2px 16px rgba(0,0,0,0.04)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
                <GraduationCap size={15} color="#E62B1E" />
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, letterSpacing: '2px', color: '#E62B1E', textTransform: 'uppercase' }}>Education</span>
              </div>
              {EDUCATION.map((e, i) => (
                <div key={i} style={{ marginBottom: i < EDUCATION.length - 1 ? '16px' : 0 }}>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 600, color: '#0A0A0A', margin: '0 0 3px 0' }}>{e.degree}</p>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#6B6B6B', margin: 0 }}>{e.institution} · {e.period}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Photos (hidden on tablet, shown on desktop + mobile) */}
          {(!isTablet || isMobile) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.12)' }}>
                <img src={PROFILE.photos.working} alt="Krupal Chaudhary working"
                  style={{ width: '100%', height: isMobile ? '280px' : '340px', objectFit: 'cover', objectPosition: 'center 65%', display: 'block' }} />
              </div>
              <div style={{
                background: '#0A0A0A', borderRadius: '8px', padding: '24px 28px',
                borderLeft: '4px solid #E62B1E'
              }}>
                <Briefcase size={16} color="#E62B1E" style={{ marginBottom: '10px' }} />
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#CCCCCC', lineHeight: 1.75, margin: '0 0 12px 0' }}>
                  Started Demaze in his <strong style={{ color: '#fff' }}>third year of college</strong> with no funding, no clients - just a vision to build AI that actually works.
                </p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#5A5A5A', margin: 0 }}>Before that: traded sneakers, invested in stocks, built mental health platforms. Always building.</p>
              </div>
              <div style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.1)' }}>
                <img src={PROFILE.photos.beach} alt="Krupal Chaudhary"
                  style={{ width: '100%', height: isMobile ? '300px' : '400px', objectFit: 'cover', objectPosition: 'center 20%', display: 'block' }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default StorySection;
